import React from 'react';
import { Shield, AlertCircle, Bot, Database } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { BlogPost } from '../../types/blog';

interface BlogPostDisclaimerProps {
  post: BlogPost;
}

export function BlogPostDisclaimer({ post }: BlogPostDisclaimerProps) {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-8">
      <div className="flex items-center gap-2 mb-4">
        <Shield className="h-5 w-5 text-gray-600" />
        <h3 className="font-semibold text-gray-800">Content Disclaimer</h3>
      </div>

      <div className="space-y-4 text-sm text-gray-700">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-red-700 mb-1">Not Financial Advice</p>
            <p>
              This content is for <strong>informational purposes only</strong> and does not constitute 
              financial, investment, or trading advice. Always consult with qualified financial 
              professionals before making any financial decisions.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <Database className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-blue-700 mb-1">Data Sources</p>
            <p>
              Exchange rates and market data are obtained from publicly available sources and 
              third-party APIs. We do not guarantee the accuracy, completeness, or timeliness 
              of this information.
            </p>
          </div>
        </div>

        {post.isAutoGenerated && (
          <div className="flex items-start gap-3">
            <Bot className="h-4 w-4 text-purple-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-purple-700 mb-1">AI-Generated Content</p>
              <p>
                This article was automatically generated using artificial intelligence based on 
                current market data. Please verify all information independently before making 
                any financial decisions.
              </p>
            </div>
          </div>
        )}

        <div className="pt-3 border-t border-gray-200">
          <p className="text-xs text-gray-600">
            <strong>Risk Warning:</strong> Trading foreign exchange involves significant risk and may not be 
            suitable for all investors. Past performance is not indicative of future results. 
            Please read our complete{' '}
            <Link to="/disclaimer" className="text-blue-600 hover:text-blue-800 underline">
              disclaimer
            </Link>{' '}
            and{' '}
            <Link to="/terms" className="text-blue-600 hover:text-blue-800 underline">
              terms of service
            </Link>.
          </p>
        </div>
      </div>
    </div>
  );
}